import type { BillingCurrency } from "@talenthop/shared";
import { BILLING_CURRENCIES, formatMinor } from "@talenthop/shared";
import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { apiErrorText } from "../admin/adminUi.js";
import { Alert, ArrowLeft, Coins, Mail, ShieldCheck, Users } from "../../components/icons.js";

/**
 * Distributor partner console (0031) — visible only when the signed-in org is a distributor.
 * Two surfaces by design: Economy (child-org revenue, the partner's cut, payout history) and
 * Client orgs (list + create, parent-locked server-side). Pricing, prompts, sourcing
 * fulfilment, platform team and audit stay with TalentHop — the API refuses them anyway.
 */

/** Exact minor units, via the one shared formatter. This file used to carry its own copy of the
 *  rounding helper that was deleted from the credits surfaces — and it formats PAYOUTS, so a
 *  ₹7,770.40 settlement rendered as "₹7,770" to the partner we owe it to. Same class of
 *  shown ≠ transacted, on somebody else's money. */
function money(minor: number, cur: string): string {
  return formatMinor(minor, (BILLING_CURRENCIES as readonly string[]).includes(cur)
    ? (cur as BillingCurrency)
    : "INR");
}

function PartnerHeader({ title, sub, action }: { title: string; sub: string; action?: ReactNode }): ReactNode {
  return (
    <div className="page-h">
      <div>
        <h1>{title}</h1>
        <div className="sub">{sub}</div>
      </div>
      {action}
    </div>
  );
}

/**
 * The partner API refuses (403) anyone who isn't an admin of a distributor-partner org. That used
 * to render as a bare pink "owner-only" bar on an empty page (UX audit top-15 #4) — now a real
 * empty state: what the page is, why it's not available, who to ask, and a way back. Any other
 * failure keeps the plain error banner, since it's genuinely an error.
 */
function PartnerUnavailable({ error, title }: { error: unknown; title: string }): ReactNode {
  if (!(error instanceof ApiError && error.status === 403)) {
    return (
      <section className="t1s-page t1s-partner">
        <PartnerHeader title={title} sub="Distributor partner console" />
        <div className="banner crit"><Alert /><span>{apiErrorText(error, "Couldn't load the partner console. Please retry.")}</span></div>
      </section>
    );
  }
  return (
    <section className="t1s-page t1s-partner">
      <PartnerHeader title={title} sub="Distributor partner console" />
      <div className="card t1s-empty" role="status">
        <div className="t1s-empty-icon"><ShieldCheck /></div>
        <h2>The partner console is for distributor partners</h2>
        <p>
          It shows commission and client organisations for TalentHop distributor partners, and is open to
          their admins only. Your account doesn't have access — if you think it should, ask your
          organisation's super admin or write to <b>partners@talenthopai.com</b>.
        </p>
        <div className="t1s-actions">
          <Link to="/app" className="btn primary"><ArrowLeft /> Back to dashboard</Link>
          <a className="btn" href="mailto:partners@talenthopai.com?subject=TalentHop%20partner%20programme">
            <Mail /> Contact partnerships
          </a>
        </div>
        <div className="t1s-code">Error 403 · no access</div>
      </div>
    </section>
  );
}

interface EconomyResponse {
  partner: { name: string; sharePct: number; currency: string; symbol: string };
  orgs: { orgId: string; name: string; currency: string; revenueMinor: number; cutMinor: number }[];
  totals: { revenueMinor: number; cutMinor: number };
  payouts: {
    periodStart: string; currency: string; revenueMinor: number; payoutMinor: number;
    status: string; paidAt: string | null; method: string | null;
  }[];
}

export function PartnerEconomy(): ReactNode {
  const q = useQuery({ queryKey: ["partner-economy"], queryFn: () => api.get<EconomyResponse>("/partner/economy") });
  const d = q.data;
  const title = "Partner economy";
  if (q.isLoading) {
    return (
      <section className="t1s-page t1s-partner">
        <PartnerHeader title={title} sub="Your commission from client organisations" />
        <p className="mut">Loading partner economy…</p>
      </section>
    );
  }
  if (q.isError || !d) return <PartnerUnavailable error={q.error} title={title} />;
  return (
    <section className="t1s-page t1s-partner">
      <PartnerHeader title={title} sub={`${d.partner.name} · your commission from client organisations`} />
      <div className="t1s-kpis">
        <div className="card">
          <div className="k">Sub-org revenue · MTD</div>
          <div className="v tnum">{money(d.totals.revenueMinor, d.partner.currency)}</div>
          <div className="s">{d.orgs.length} client org{d.orgs.length === 1 ? "" : "s"}</div>
        </div>
        <div className="card">
          <div className="k">Your commission · {d.partner.sharePct}%</div>
          <div className="v good tnum">{money(d.totals.cutMinor, d.partner.currency)}</div>
          <div className="s">accrues on every purchase</div>
        </div>
        <div className="card">
          <div className="k">Payouts</div>
          <div className="v">Monthly</div>
          <div className="s">settled on the 1st, your preferred method</div>
        </div>
      </div>

      <div className="banner t1s-neutral">
        <Coins />
        <span>
          Your share is <b>{d.partner.sharePct}% of everything your client orgs purchase</b> — packs, tiers
          and top-ups, in any currency. The rate is set by TalentHop; write to <b>partners@talenthopai.com</b> to review it.
        </span>
      </div>

      <div className="card">
        <div className="card-h"><h3>Where it comes from</h3><span className="mut sm">live · this month</span></div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Org</th><th>Currency</th><th>Purchases · MTD</th><th style={{ textAlign: "right" }}>Your {d.partner.sharePct}%</th></tr></thead>
            <tbody>
              {d.orgs.length === 0 && (
                <tr><td colSpan={4} className="mut sm" style={{ padding: 14 }}>No client orgs yet — create your first from Client orgs.</td></tr>
              )}
              {d.orgs.map((o) => (
                <tr key={o.orgId}>
                  <td style={{ fontWeight: 650 }}>{o.name}</td>
                  <td>{o.currency}</td>
                  <td className="tnum">{money(o.revenueMinor, o.currency)}</td>
                  <td className="tnum" style={{ textAlign: "right", fontWeight: 700, color: "var(--good)" }}>
                    {money(o.cutMinor, o.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-h"><h3>Payout history</h3><span className="mut sm">phase 1 — logged by TalentHop</span></div>
        <div className="card-b">
          {d.payouts.length === 0 && <span className="mut sm">First payout accrues this month.</span>}
          {d.payouts.map((p) => (
            <div key={p.periodStart} className="t1s-payout">
              <span className="tnum" style={{ flex: 1 }}>{p.periodStart}</span>
              <span className="mut sm">{p.method ?? ""}</span>
              <span className={`pill ${p.status === "paid" ? "v" : "n"}`} style={{ textTransform: "capitalize" }}>{p.status}</span>
              <span className="tnum" style={{ fontWeight: 700 }}>{money(p.payoutMinor, p.currency)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface PartnerOrgsResponse {
  sharePct: number;
  orgs: {
    orgId: string; name: string; currency: string; balance: number; members: number;
    revenueMinor: number; cutMinor: number; createdAt: string;
  }[];
}

export function PartnerOrgs(): ReactNode {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["partner-orgs"], queryFn: () => api.get<PartnerOrgsResponse>("/partner/orgs") });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [region, setRegion] = useState("eu-west");
  const create = useMutation({
    mutationFn: () => api.post("/partner/orgs", { name: name.trim(), region, currency, adminEmail: email.trim() }),
    onSuccess: () => {
      setOpen(false); setName(""); setEmail("");
      void qc.invalidateQueries({ queryKey: ["partner-orgs"] });
      void qc.invalidateQueries({ queryKey: ["partner-economy"] });
    },
  });
  const d = q.data;
  const title = "Client orgs";
  if (q.isLoading) {
    return (
      <section className="t1s-page t1s-partner">
        <PartnerHeader title={title} sub="Organisations you create and manage" />
        <p className="mut">Loading your client orgs…</p>
      </section>
    );
  }
  if (q.isError || !d) return <PartnerUnavailable error={q.error} title={title} />;
  return (
    <section className="t1s-page t1s-partner">
      <PartnerHeader
        title={title}
        sub={`Organisations you create and manage — you earn ${d.sharePct}% of what they buy`}
        action={<button type="button" className="btn primary" onClick={() => setOpen(true)}><Users /> New org</button>}
      />
      <div className="card">
        <div className="card-h">
          <h3>My client orgs</h3>
          <span className="mut sm">{d.orgs.length} org{d.orgs.length === 1 ? "" : "s"}</span>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Org</th><th>Currency</th><th>Wallet</th><th>Purchases · MTD</th><th>Your {d.sharePct}%</th><th>Members</th></tr></thead>
            <tbody>
              {d.orgs.length === 0 && (
                <tr><td colSpan={6} className="mut sm" style={{ padding: 14 }}>
                  No orgs yet — create your first client org and earn on everything they buy.
                </td></tr>
              )}
              {d.orgs.map((o) => (
                <tr key={o.orgId}>
                  <td style={{ fontWeight: 650 }}>{o.name}</td>
                  <td>{o.currency}</td>
                  <td className="tnum">{o.balance} cr</td>
                  <td className="tnum">{money(o.revenueMinor, o.currency)}</td>
                  <td className="tnum" style={{ fontWeight: 700, color: "var(--good)" }}>{money(o.cutMinor, o.currency)}</td>
                  <td className="tnum">{o.members}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pad mut sm" style={{ paddingTop: 8 }}>
          You create and manage these orgs and earn <b>{d.sharePct}%</b> of every purchase they make.
          Pricing, AI prompts, sourcing fulfilment and the platform audit stay with TalentHop.
        </div>
      </div>

      {open && (
        <div className="rq-modal-scrim" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
          <div className="rq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rq-modal-h">
              <div className="t">Create a client org</div>
              <div className="d">They start with a free starter allowance; your {d.sharePct}% applies to everything they buy (excluding GST).</div>
            </div>
            <div className="rq-modal-b t1s-partner t1s-form">
              <label htmlFor="po-name">Company name</label>
              <input id="po-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Helsing Recruitment GmbH" />
              <label htmlFor="po-email">Admin email — gets the invite</label>
              <input id="po-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@company.com" />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                <div className="t1s-form">
                  <label htmlFor="po-region">Region</label>
                  <select id="po-region" className="input" value={region} onChange={(e) => setRegion(e.target.value)}>
                    <option value="eu-west">EU West</option>
                    <option value="in-central">India</option>
                    <option value="us-east">US East</option>
                  </select>
                </div>
                <div className="t1s-form">
                  <label htmlFor="po-cur">Billing currency</label>
                  <select id="po-cur" className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    {["EUR", "GBP", "USD", "INR"].map((cur) => <option key={cur} value={cur}>{cur}</option>)}
                  </select>
                </div>
              </div>
              {create.isError && (
                <div className="banner crit"><Alert /><span>{apiErrorText(create.error, "Couldn't create the org.")}</span></div>
              )}
            </div>
            <div className="rq-modal-f">
              <button type="button" className="rq-btn ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button
                type="button"
                className="rq-btn primary"
                disabled={create.isPending || name.trim().length < 2 || !email.includes("@")}
                onClick={() => create.mutate()}
              >
                {create.isPending ? "Creating…" : "Create & send invite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
